import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { useMotion } from '../../hooks/useMotion.js';
import { reducedMotion } from '../../hooks/useLenis.js';
import './Services.css';

const specialistServices = [
  {
    num: '01',
    tag: 'Retirement & Wealth',
    title: 'Retirement Planning',
    short: 'Achieve sustained financial security in your retirement years.',
    desc: 'Our retirement planning services help you achieve financial security in your retirement years. We develop a personalised plan that aligns with your goals and ensures a steady income stream.',
    image: '/media/retirement-savings-plans-text-on-puzzle-with-money-2026-08-05-06-59-58-utc.webp',
    tags: ['Personalised Plan', 'Steady Income', 'Longevity Protection'],
  },
  {
    num: '02',
    tag: 'Legacy Preservation',
    title: 'Estate Planning',
    short: 'Ensure your wealth is preserved and transferred according to your wishes.',
    desc: 'Our estate planning services ensure your wealth is preserved and transferred according to your wishes. We assist with wills, trusts and other legal arrangements to protect your assets and provide for your loved ones.',
    image: '/media/financial-planning-for-family-home-ownership-conc-2026-03-20-04-05-42-utc.webp',
    tags: ['Wills & Trusts', 'Asset Protection', 'Generational Transfer'],
  },
  {
    num: '03',
    tag: 'Alternative Assets',
    title: 'Private-Fund Management',
    short: 'Strategic asset allocation and risk management across diverse portfolios.',
    desc: 'We manage diverse portfolios including hedge funds, private equity and real estate, employing strategic asset allocation and risk management techniques.',
    image: '/media/double-exposure-digital-graphs-with-stacked-coins.webp',
    tags: ['Hedge Funds', 'Private Equity', 'Real Estate'],
  },
  {
    num: '04',
    tag: 'Impact & Giving',
    title: 'Philanthropic Advisory',
    short: 'Develop and execute purposeful charitable giving strategies.',
    desc: 'For clients interested in making a positive impact, we help you develop and execute charitable giving strategies. We assist with creating foundations, managing donations and aligning your philanthropic efforts with your values and goals.',
    image: '/media/businessman-analyzing-financial-data-with-tablet.webp',
    tags: ['Charitable Strategies', 'Private Foundations', 'Values Alignment'],
  },
];

export const Services = () => {
  useMotion();
  const mandateSectionRef = useRef(null);
  const logoMaskRef = useRef(null);

  useEffect(() => {
    document.title = 'Our Services | ASTRID FIRST Wealth Advisory';
  }, []);

  useEffect(() => {
    if (reducedMotion()) return;

    const section = mandateSectionRef.current;
    const mask = logoMaskRef.current;
    if (!section || !mask) return;

    const mm = gsap.matchMedia();

    // Desktop and Tablet (>= 768px):
    mm.add('(min-width: 768px)', () => {
      gsap.fromTo(
        mask,
        {
          x: -25,
          opacity: 0.45,
        },
        {
          x: 35,
          opacity: 0.95,
          ease: 'power1.out',
          scrollTrigger: {
            trigger: section,
            start: 'top 80%',
            end: 'bottom 20%',
            scrub: 1.2,
          },
        }
      );
    });

    return () => {
      mm.revert();
    };
  }, []);

  const toggleCardSplit = (e) => {
    if (e.target.closest('a')) return;
    const card = e.currentTarget;
    const isSplit = card.classList.toggle('is-split');
    card.setAttribute('aria-expanded', isSplit ? 'true' : 'false');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleCardSplit(e);
    }
  };

  return (
    <div className="services-page">
      <header className="page-head">
        <div className="page-head-inner">
          <p className="crumb">
            <a href="/">Home</a> / Our Services
          </p>
          <h1 data-reveal="chars">Our Services</h1>
          <p className="lead" data-reveal="fade">
            Two ways to work with us, and the planning that surrounds them.
          </p>
        </div>
      </header>

      <div className="spine">
        <section className="section shell">
          <div className="track">
            <div className="track-head track-head--sticky">
              <p className="eyebrow">Where we start</p>
              <h2 data-reveal="lines">Our Services</h2>
            </div>
            <div className="prose">
              <p className="lead" data-reveal="fade">
                Your journey begins with us understanding your investment
                priorities, needs and goals.
              </p>
              <p data-reveal="fade">
                According to your risk appetite, preferences and objectives, we
                propose tailored solutions from a best-of-breed product
                selection — one based on independent tracking of a large array of
                funds, structured products and insurance options.
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* Choose your mandate section with skyline background, 50% black overlay, and left logo mask */}
      <section
        ref={mandateSectionRef}
        className="section mandate-section stage"
        id="choose-mandate"
        aria-labelledby="mandate-title"
      >
        <div className="mandate-bg-wrap" aria-hidden="true">
          <img
            src="/media/singapore-by-night-2026-04-14-00-40-27-utc.webp"
            alt="Singapore Skyline at Night"
            className="mandate-bg-img"
          />
          <div className="mandate-overlay" />
          <div className="mandate-right-overlay" />
        </div>

        <div className="shell mandate-shell">
          <div className="mandate-grid">
            {/* Left side: Logo image mask with 100% height of this section */}
            <div className="mandate-visual-col" aria-hidden="true">
              <div className="mandate-logo-sticky">
                <div ref={logoMaskRef} className="mandate-logo-mask" />
              </div>
            </div>

            {/* Right side: Title and content */}
            <div className="mandate-content-col">
              <div className="mandate-header">
                <p className="eyebrow mandate-eyebrow">Choose your mandate</p>
                <h2
                  id="mandate-title"
                  className="mandate-title"
                  data-reveal="lines"
                >
                  How the relationship is set up
                </h2>
              </div>

              <div className="mandate-cards" data-reveal="group">
                <article className="mandate-card">
                  <div className="mandate-card-header">
                    <span className="mandate-card-badge">01</span>
                    <h3>Discretionary</h3>
                  </div>
                  <p>
                    In a discretionary mandate, you delegate the investment
                    management to ASTRID FIRST. We manage your investments based
                    on your goals, expectations and risk appetite. This benefits
                    clients through a more systematic and disciplined approach,
                    while taking the psychological and emotional pressure out of
                    day-to-day decisions.
                  </p>
                </article>

                <article className="mandate-card">
                  <div className="mandate-card-header">
                    <span className="mandate-card-badge">02</span>
                    <h3>Advisory</h3>
                  </div>
                  <p>
                    In an advisory mandate, you control and make all investment
                    decisions. We work with you to define the engagement process
                    so that we can be your eyes and ears in the financial
                    markets.
                  </p>
                </article>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Planning and specialist services section with horizontal scroll & split cards */}
      <section
        className="section"
        id="specialist-services"
        aria-labelledby="specialist-title"
      >
        <div
          className="shell"
          style={{ marginBottom: 'clamp(2rem, 5vw, 3.5rem)' }}
        >
          <p className="eyebrow">Beyond the portfolio</p>
          <h2 id="specialist-title" data-reveal="lines">
            Planning and specialist services
          </h2>
          <p
            className="lead"
            data-reveal="fade"
            style={{ maxWidth: '44rem', marginTop: '1.2rem' }}
          >
            Comprehensive wealth solutions extending past portfolio management
            to protect your legacy, retirement, and multi-generational vision.
          </p>
        </div>

        <div className="work-viewport" data-horizontal>
          <div className="work-track" data-horizontal-track>
            {specialistServices.map((service) => (
              <article
                key={service.num}
                className="work-card"
                data-split-card
                tabIndex={0}
                role="button"
                aria-expanded="false"
                onClick={toggleCardSplit}
                onKeyDown={handleKeyDown}
                title={`${service.title} — click to reveal details`}
              >
                {/* Background Layer: Detailed Explanation */}
                <div className="work-card-back">
                  <div className="work-back-header">
                    <span className="work-card-num">{service.num}</span>
                    <span className="work-back-pill">Specialist Service</span>
                  </div>
                  <div className="work-back-body">
                    <h3 className="work-back-title">{service.title}</h3>
                    <p className="work-back-lead">{service.short}</p>
                    <p className="work-back-desc">{service.desc}</p>
                    <div className="work-back-tags">
                      {service.tags.map((t) => (
                        <span key={t}>{t}</span>
                      ))}
                    </div>
                  </div>
                  <div className="work-back-foot">
                    <a
                      className="btn btn--bare"
                      href="/contact"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Discuss with an advisor &rarr;
                    </a>
                  </div>
                </div>

                {/* Front Layer: Split Top (Title) & Split Bottom (Photo) */}
                <div className="work-card-front" aria-hidden="true">
                  <div className="work-split-top">
                    <div className="work-split-top-content">
                      <div className="work-card-meta">
                        <span className="work-card-num">{service.num}</span>
                        <span className="work-card-tag">{service.tag}</span>
                      </div>
                      <h3 className="work-front-title">{service.title}</h3>
                    </div>
                    <div className="work-split-seam-top">
                      <span>Scroll to uncover</span>
                      <span>&darr; &uarr;</span>
                    </div>
                  </div>

                  <div className="work-split-bottom">
                    <div className="work-split-photo-wrap">
                      <img
                        src={service.image}
                        alt={service.title}
                        loading="lazy"
                        className="work-split-img"
                      />
                      <div className="work-split-photo-overlay" />
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};